import { API_BASE_URL } from '../config';
import type { UploadedAsset, AssetKey } from '../types';
import {
  validateFile,
  extractMediaMetadata,
  extractImageThumbnail,
} from '../utils/assetValidation';

export interface LocalAsset extends UploadedAsset {
  /** Object URL valid for this browser session */
  objectUrl: string;
}

/**
 * Creates a LocalAsset from a File without hitting the server.
 * Used in offline/preview mode — the object URL is used for the
 * Remotion player preview. The server upload happens separately.
 */
// Asset keys that are always audio regardless of the file's reported MIME type.
// An audio-only .mp4 file reports as video/mp4 in the browser, but must be
// stored as type 'audio' so AudioLayer and the renderer handle it correctly.
const AUDIO_ASSET_KEYS = new Set<AssetKey>(['voiceAudio', 'backgroundMusic']);

// File extensions that indicate an audio file even when the MIME is video/*.
const AUDIO_EXTENSIONS = new Set(['mp3', 'wav', 'ogg', 'm4a', 'aac', 'mp4']);

function resolveAssetType(file: File, key: AssetKey): import('../types').AssetType {
  if (AUDIO_ASSET_KEYS.has(key)) return 'audio';
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('audio/')) return 'audio';
  // .mp4 in a non-audio slot is a real video
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (file.type.startsWith('video/') && !AUDIO_EXTENSIONS.has(ext)) return 'video';
  if (file.type.startsWith('video/') && AUDIO_EXTENSIONS.has(ext)) return 'audio';
  return 'audio';
}

export async function createLocalAsset(
  file: File,
  key: AssetKey
): Promise<LocalAsset> {
  const objectUrl = URL.createObjectURL(file);
  const assetType = resolveAssetType(file, key);

  const meta = await extractMediaMetadata(file);
  const thumbnailUrl =
    assetType === 'image'
      ? (await extractImageThumbnail(file)) ?? undefined
      : undefined;

  return {
    id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    key,
    name: file.name,
    type: assetType,
    mimeType: file.type,
    size: file.size,
    url: objectUrl,
    objectUrl,
    thumbnailUrl,
    duration: meta?.duration,
    width: meta?.width,
    height: meta?.height,
    uploadedAt: new Date().toISOString(),
  };
}

/**
 * Uploads a File to the server and returns the persisted UploadedAsset.
 * Falls back to a local asset if the server is unreachable.
 */
export async function uploadAsset(
  file: File,
  key: AssetKey
): Promise<UploadedAsset> {
  const validation = validateFile(file, key);
  if (!validation.valid) {
    throw new Error(validation.error ?? 'Invalid file');
  }

  try {
    const form = new FormData();
    form.append('file', file);
    form.append('key', key);

    const res = await fetch(`${API_BASE_URL}/api/upload`, {
      method: 'POST',
      body: form,
    });

    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as {
        message?: string;
        error?: { message?: string };
      };
      // Server error body may be { error: { message } } or { message }
      const msg = body.message ?? body.error?.message ?? `Upload failed (${res.status})`;
      throw new Error(msg);
    }

    const data = (await res.json()) as { asset: UploadedAsset };
    return data.asset;
  } catch (err) {
    // Server unavailable — fall back to local object URL for preview-only mode
    if (err instanceof TypeError && err.message.includes('fetch')) {
      console.warn('[assetService] Server unreachable — using local asset URL');
      return createLocalAsset(file, key);
    }
    throw err;
  }
}

/**
 * Revokes an object URL to free memory. Safe to call on server assets (no-op).
 */
export function revokeAssetUrl(asset: UploadedAsset): void {
  if ('objectUrl' in asset) {
    URL.revokeObjectURL((asset as LocalAsset).objectUrl);
  }
}
